import produce, { Immer } from 'immer';
import { BROWSER_ADD_ASSET_WITH_CAMPAIGN, EMAIL_BROWSER, EMAIL_BROWSER_ADD_ARTICLES_TO_EMAIL, EMAIL_BROWSER_ADD_EMAIL } from 'constants/actionTypes';
import {
  ASSET_DELETE,
  ASSET_DETAILS,
  ASSET_DETAILS_ADD_NEW_DRAFT,
  ASSET_DETAILS_NAME,
  ASSET_DETAILS_REMOVE_DRAFT,
  ASSET_DETAILS_UPDATE_DRAFT,
  ASSET_DETAILS_UPDATE_KEYWORDS,
  ASSET_DETAILS_UPDATE_NAME,
  ASSET_LIST,
  ASSET_SEARCH_SENT_EDITIONS,
  ASSET_SENT_EDITIONS,
  EMAIL_LIST,
  GET_ASSET_REMINDER,
  POST_ASSET_REMINDER,
  PREVIOUSLY_SENT_CONTENT,
} from 'constants/types';
import { IAssetItem, IDraftEmailItem } from 'modules/Content/interfaces';
import { IPreviouslySentEditions, IPublishReminder, IVersion } from '../interfaces';
import { ASSET_TYPE } from '../../../constants/index';

const emailsInitialState = {
  campaignName: '',
  campaignKeywords: Array<string>(),
  noKeywords: false,
  graph: {
    loading: false,
    data: {},
    startDate: '',
    endDate: '',
    userId: 'default',
    isEmpty: false,
  },
  draftEmails: { data: [] as IDraftEmailItem[], loading: false },
  sentEmails: { data: [] as any, loading: false, total: 0 },
  testEmails: { data: [] as any, loading: false, total: 0 },
  allEmails: { data: [] as any, loading: false, total: 0 },
  allSentEmails: { data: [] as any, loading: false, total: 0 },
  scheduledEmails: { data: [] as any, loading: false, total: 0 },
  success: true,
  message: '',
  user: { label: 'by User', value: 'default' },
  userFilter: { label: 'by User', value: 'default' },
  dateFilter: { value: 'qtr', label: '1 QTR' },
  datesObj: { start_date: '', end_date: '' },
  userId: '',
  emailTypes: { label: 'All Types', value: 'all' },
  hasMoreSent: true,
  hasMoreTest: true,
  hasMoreAll: true,
  hasMoreAllSent: true,
  hasMoreScheduled: true,
  emailCreationCampaignKeywords: [],
  emailCreatedInExistingCampaign: false,
  campaignId: '',
  cloningInProgress: false,
  reset: false,
  tabState: '',
};

interface emailAction {
  type: string;
  payload: any;
  campaignName: string;
  campaignKeywords: string[];
  noKeywords: boolean;
  graph: any;
  isFiltered: boolean;
  limit?: number;
  campaignId?: string;
  totalEmails?: number;
  isPaginated?: boolean;
}

export const emailsReducer = (state = emailsInitialState, action: emailAction) => {
  const newState = { ...state };
  switch (action.type) {
    // ###########################################
    // #  CASES RELATED TO PARENT CAMPAIGN       #
    // ###########################################

    case EMAIL_LIST.SET_CAMPAIGN_DETAILS:
      newState.campaignName = action.campaignName;
      newState.campaignId = action.campaignId ?? '';
      newState.campaignKeywords = [...action.campaignKeywords];
      newState.noKeywords = action.campaignKeywords.length === 0 ? true : false;
      return newState;

    case EMAIL_LIST.RESET_CAMPAIGN_FILTERS:
      newState.user = { label: 'by User', value: 'default' };
      newState.userFilter = { label: 'by User', value: 'default' };
      newState.dateFilter = { value: 'qtr', label: '1 QTR' };
      newState.emailTypes = { label: 'All Types', value: 'all' };
      newState.userId = 'default';
      return newState;

    // * update campaign name
    case EMAIL_LIST.UPDATE_CAMPAIGN_NAME:
      newState.campaignName = action.payload?.campaignName;
      return newState;

    // * update campaign keywords
    case EMAIL_LIST.UPDATE_CAMPAIGN_KEYWORDS:
      newState.campaignKeywords = [...action.payload];
      newState.noKeywords = action.payload.length === 0 ? true : false;
      return newState;

    case EMAIL_LIST.RESET_CAMPAIGN_DETAILS:
      newState.campaignKeywords = [];
      newState.noKeywords = false;
      newState.campaignName = '';
      return newState;

    case EMAIL_LIST.EMAIL_IN_EXISTING_CAMPAIGN:
      newState.emailCreatedInExistingCampaign = action.payload;
      if (action?.campaignId) newState.campaignId = action?.campaignId;
      else newState.campaignId = '';
      return newState;

    case EMAIL_LIST.CAMPAIGN_KEYWORDS_WHEN_CREATING_NEW_EMAIL:
      newState.emailCreationCampaignKeywords = action.payload;
      return newState;
    case EMAIL_LIST.RESET_CAMPAIGN_KEYWORDS_WHEN_CREATING_NEW_EMAIL:
      newState.emailCreationCampaignKeywords = [];
      return newState;

    // ###########################################
    // #  CASES RELATED TO DRAFT EMAILS          #
    // ###########################################

    case EMAIL_LIST.RESET:
      newState.reset = action.payload;
      return newState;

    case EMAIL_LIST.DRAFT_LOAD:
      newState.draftEmails.loading = true;
      return newState;

    case EMAIL_LIST.DRAFT_SUCCESS:
      newState.draftEmails.loading = false;
      newState.draftEmails.data = [...action.payload];
      return newState;

    case EMAIL_LIST.DRAFT_UPDATE:
      let updatedDraftEmails = newState.draftEmails.data.filter((email: any) => email.emailId.toString() !== action.payload.toString());
      newState.draftEmails.data = updatedDraftEmails;
      return newState;

    case EMAIL_LIST.CLONE_DRAFT_IN_PROGRESS:
      newState.cloningInProgress = action.payload;
      return newState;

    case EMAIL_LIST.CLONE_DRAFT_SUCCESS:
      newState.draftEmails.loading = false;
      let emailList = newState.draftEmails.data;

      let indexOfEmail = emailList.findIndex((item) => item.emailId.toString() === action.payload.draftEmailId.toString());

      const resData = action.payload.data;

      let clonedEmailData = {} as IDraftEmailItem;

      clonedEmailData.emailId = resData.email_id;
      clonedEmailData.name = resData.name;
      clonedEmailData.type = resData.email_type;
      clonedEmailData.lastUpdate = 'just now';
      clonedEmailData.thumbnails = emailList[indexOfEmail].thumbnails;
      clonedEmailData.workflowStatus = '';
      if (resData.generation_id) {
        clonedEmailData.generationId = resData.generation_id;
        clonedEmailData.aiColor = resData.ai_color;
        clonedEmailData.aiType = resData.ai_type;
      }

      newState.draftEmails.data.splice(indexOfEmail + 1, 0, clonedEmailData);
      newState.draftEmails.data = [...newState.draftEmails.data];
      newState.cloningInProgress = false;

      return newState;

    case EMAIL_LIST.DRAFT_EMAILS_RESET:
      newState.draftEmails = { data: [] as IDraftEmailItem[], loading: false };
      return newState;

    case EMAIL_LIST.DRAFT_EMAILS_RESET_LOAD:
      newState.draftEmails = { data: [] as IDraftEmailItem[], loading: true };
      return newState;

    case EMAIL_LIST.DRAFT_RESET:
      newState.campaignName = '';
      newState.campaignKeywords = Array<string>();
      newState.noKeywords = false;
      newState.graph = {
        loading: false,
        data: {},
        startDate: '',
        endDate: '',
        userId: 'default',
        isEmpty: false,
      };
      newState.draftEmails = { data: [] as IDraftEmailItem[], loading: false };
      newState.sentEmails = { data: [] as any, loading: false, total: 0 };
      newState.hasMoreSent = true;
      return newState;

    case EMAIL_LIST.DRAFT_FAIL:
      newState.draftEmails.loading = false;
      return newState;

    case EMAIL_LIST.SET_USER:
      newState.user = action.payload;
      return newState;

    case EMAIL_LIST.SET_USER_FILTER:
      newState.userFilter = action.payload;
      return newState;

    case EMAIL_LIST.SET_DATE:
      newState.dateFilter = action.payload;
      return newState;

    case EMAIL_LIST.SET_EMAIL_TYPE:
      newState.emailTypes = action.payload;
      return newState;

    case EMAIL_LIST.STORE_DATES:
      newState.datesObj = action.payload;
      return newState;

    case EMAIL_LIST.STORE_USER:
      newState.userId = action.payload;
      return newState;

    // ###########################################
    // #  CASES RELATED TO EMAIL BUILDER         #
    // ###########################################

    //* Add new email in a campaign
    case EMAIL_BROWSER_ADD_EMAIL.ADD_SUCCESS:
      // checking if the Emails page smartsouce is open then include the recently created email in dashboard
      // otherwise it will not be included immediately rather it will be included in when fetched from BE
      if (action.payload?.campaignId && action.payload?.isIncludeInEmailsDashboard) {
        newState.draftEmails.data = [action.payload?.email, ...newState.draftEmails.data];
      }
      return newState;

    case EMAIL_LIST.ADD_EMAIL_SUCCESS:
      return newState;

    case EMAIL_LIST.ADD_EMAIL_FAIL:
      return newState;

    case EMAIL_LIST.ADD_EMAIL_RESET:
      return newState;

    // //* delete an email from a campaign
    case EMAIL_BROWSER.REMOVE_EMAIL:
      newState.draftEmails.data = newState.draftEmails.data?.filter((item: any) => item.emailId !== action.payload?.emailId);
      return newState;

    //* update any email's name inside a campaign
    case EMAIL_BROWSER.SET_EMAIL_NAME:
      const emailIndexToBeUpdated = newState.draftEmails?.data?.findIndex((email: any) => action.payload.emailId === email.emailId);
      if (emailIndexToBeUpdated !== -1) {
        const newData = { ...newState.draftEmails.data[emailIndexToBeUpdated], name: action.payload.emailName };
        newState.draftEmails.data[emailIndexToBeUpdated] = newData;
        // * if we need to reorder the updated email to first position uncomment this
        // newState.data = [
        //   newState.data[emailIndexToBeUpdated],
        //   ...newState?.data.filter((item) => item.emailId !== action.payload.emailId),
        // ];
      }

      return newState;

    //* update email's articles count based on curation
    case EMAIL_BROWSER_ADD_ARTICLES_TO_EMAIL.SET_PREVIEW:
      const { emailId, articles_count, last_update } = action.payload;
      let indexFound = -1;
      newState.draftEmails.data = newState.draftEmails?.data?.map((email: any, index: number) => {
        let item = { ...email };
        if (item.emailId === emailId) {
          item.articlesCount = articles_count;
          item.showUndo = true;
          item.lastUpdate = last_update;
          item.newlyCreated = true;
          indexFound = index;
        }
        return item;
      });
      // * if we need to reorder the updated email to first position uncomment this
      // if (indexFound >= 0) {
      //   newState.data = [
      //     newState?.data[indexFound],
      //     ...newState?.data.filter((item) => item.emailId !== action.payload.emailId),
      //   ];
      // }
      return newState;

    case EMAIL_LIST.DRAFT_UPDATE_IMAGES:
      const { images, email_id } = action.payload;
      let drafts = [...newState.draftEmails.data];
      let ind = newState.draftEmails.data.findIndex((item) => item.emailId === email_id);
      if (ind >= 0) {
        let item = { ...drafts[ind] };
        item.thumbnails = [...images, ...(item.thumbnails ? item.thumbnails : [])];
        drafts[ind] = item;
        newState.draftEmails.data = [...drafts];
      }
      return newState;

    // ###########################################
    // cases related to email analytics graph    #
    // ###########################################

    case EMAIL_LIST.GRAPH_SUCCESS:
      newState.graph.loading = false;
      newState.graph.data = action.graph.data;
      newState.graph.startDate = action.graph.start_date;
      newState.graph.endDate = action.graph.end_date;
      newState.graph.userId = action.graph.user_id;
      return newState;

    case EMAIL_LIST.GRAPH_LOAD:
      newState.graph.loading = true;
      newState.graph.data = {};
      return newState;

    case EMAIL_LIST.GRAPH_FAIL:
      newState.graph.loading = false;
      return newState;

    case EMAIL_LIST.GRAPH_SET_DATES:
      if (action.graph.start_date && action.graph.end_date) {
        newState.graph.startDate = action.graph.start_date;
        newState.graph.endDate = action.graph.end_date;
      }
      return newState;

    case EMAIL_LIST.GRAPH_SET_USER:
      if (action.graph.user_id) newState.graph.userId = action.graph.user_id;
      return newState;

    case EMAIL_LIST.GRAPH_CLEAR:
      newState.graph.loading = false;
      newState.graph.data = {};
      newState.graph.startDate = '';
      newState.graph.endDate = '';
      newState.graph.userId = 'default';
      newState.graph.isEmpty = false;
      return newState;

    // ###########################################
    // CASES RELATED TO SENT EMAILS              #
    // ###########################################

    case EMAIL_LIST.SENT_LOAD:
      newState.sentEmails.loading = true;
      return newState;
    case EMAIL_LIST.SENT_LOAD_OFF:
      newState.sentEmails.loading = false;
      return newState;

    case EMAIL_LIST.SENT_SUCCESS:
      newState.sentEmails.loading = false;
      if (action.isFiltered === true || action.payload.reset === true) {
        newState.sentEmails.data = [...action.payload];
      } else {
        newState.sentEmails.data = [...newState.sentEmails.data, ...action.payload];
      }
      if (action.limit && action.payload.length < action.limit) {
        newState.hasMoreSent = false;
      } else {
        newState.hasMoreSent = true;
      }
      if (action.totalEmails) newState.sentEmails.total = action.totalEmails;
      return newState;

    case EMAIL_LIST.SENT_FAIL:
      newState.sentEmails.loading = false;
      newState.sentEmails.data = [];
      newState.sentEmails.total = 0;
      return newState;

    case EMAIL_LIST.SENT_RESET:
      newState.sentEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.hasMoreSent = true;
      return newState;

    // ###########################################
    // CASES RELATED TO TEST EMAILS              #
    // ###########################################

    case EMAIL_LIST.TEST_LOAD:
      newState.testEmails.loading = true;
      return newState;
    case EMAIL_LIST.TEST_LOAD_OFF:
      newState.testEmails.loading = false;
      return newState;

    case EMAIL_LIST.TEST_SUCCESS:
      newState.testEmails.loading = false;
      if (action.isFiltered === true || action.payload.reset === true) {
        newState.testEmails.data = [...action.payload];
      } else {
        newState.testEmails.data = [...newState.testEmails.data, ...action.payload];
      }
      if (action.limit && action.payload.length < action.limit) {
        newState.hasMoreTest = false;
      } else {
        newState.hasMoreTest = true;
      }
      if (action.totalEmails) newState.testEmails.total = action.totalEmails;
      return newState;

    case EMAIL_LIST.TEST_FAIL:
      newState.testEmails.loading = false;
      newState.testEmails.data = [];
      newState.testEmails.total = 0;
      return newState;

    case EMAIL_LIST.TEST_RESET:
      newState.testEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.hasMoreTest = true;
      return newState;
    // ###########################################
    // CASES RELATED TO ALL EMAILS              #
    // ###########################################

    case EMAIL_LIST.ALL_LOAD:
      newState.allEmails.loading = true;
      return newState;
    case EMAIL_LIST.ALL_LOAD_OFF:
      newState.allEmails.loading = false;
      return newState;

    case EMAIL_LIST.ALL_SUCCESS:
      newState.allEmails.loading = false;
      if (action.isFiltered === true || action.payload.reset === true) {
        newState.allEmails.data = [...action.payload];
      } else {
        newState.allEmails.data = [...newState.allEmails.data, ...action.payload];
      }
      if (action.limit && action.payload.length < action.limit) {
        newState.hasMoreAll = false;
      } else {
        newState.hasMoreAll = true;
      }
      if (action.totalEmails) newState.allEmails.total = action.totalEmails;
      return newState;

    case EMAIL_LIST.ERROR_SEARCH_TERM:
      newState.success = action.payload.success;
      newState.message = action.payload.message;
      return newState;

    case EMAIL_LIST.ERROR_SEARCH_TERM_LOADING:
      newState.allEmails.loading = false;
      newState.allSentEmails.loading = false;
      newState.testEmails.loading = false;
      newState.scheduledEmails.loading = false;
      newState.sentEmails.loading = false;
      newState.hasMoreTest = false;
      newState.hasMoreScheduled = false;
      newState.hasMoreSent = false;
      newState.hasMoreAll = false;
      newState.hasMoreAllSent = false;
      return newState;

    case EMAIL_LIST.ALL_FAIL:
      newState.allEmails.loading = false;
      newState.allEmails.data = [];
      newState.allEmails.total = 0;
      return newState;

    case EMAIL_LIST.ALL_RESET:
      newState.allEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.hasMoreAll = true;
      return newState;

    // ###########################################
    // CASES RELATED TO ALL EMAILS              #
    // ###########################################

    case EMAIL_LIST.ALL_SENT_LOAD:
      newState.allSentEmails.loading = true;
      return newState;
    case EMAIL_LIST.ALL_SENT_LOAD_OFF:
      newState.allSentEmails.loading = false;
      return newState;

    case EMAIL_LIST.ALL_SENT_SUCCESS:
      newState.allSentEmails.loading = false;
      if (action.isFiltered === true || action.payload.reset === true) {
        newState.allSentEmails.data = [...action.payload];
      } else {
        newState.allSentEmails.data = [...newState.allSentEmails.data, ...action.payload];
      }
      if (action.limit && action.payload.length < action.limit) {
        newState.hasMoreAllSent = false;
      } else {
        newState.hasMoreAllSent = true;
      }
      if (action.totalEmails) newState.allSentEmails.total = action.totalEmails;
      return newState;

    case EMAIL_LIST.ALL_SENT_FAIL:
      newState.allSentEmails.loading = false;
      newState.allSentEmails.data = [];
      newState.allSentEmails.total = 0;
      return newState;

    case EMAIL_LIST.ALL_SENT_RESET:
      newState.allSentEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.hasMoreAllSent = true;
      return newState;

    // ###########################################
    // CASES RELATED TO SCHEDULED EMAILS              #
    // ###########################################

    case EMAIL_LIST.SCHEDULED_LOAD:
      newState.scheduledEmails.loading = true;
      return newState;

    case EMAIL_LIST.SCHEDULED_LOAD_OFF:
      newState.scheduledEmails.loading = false;
      return newState;

    case EMAIL_LIST.SCHEDULED_SUCCESS:
      newState.scheduledEmails.loading = false;
      newState.scheduledEmails.total = newState.scheduledEmails.total ? newState.scheduledEmails.total : action.totalEmails ?? 0;
      if (action.isPaginated === false) {
        newState.hasMoreScheduled = false;
        newState.scheduledEmails.data = [...action.payload];
      } else {
        newState.scheduledEmails.data = [...newState.scheduledEmails.data, ...action.payload];
        if (action.limit && action.payload.length < action.limit) {
          newState.hasMoreScheduled = false;
        } else {
          newState.hasMoreScheduled = true;
        }
      }
      return newState;

    case EMAIL_LIST.SCHEDULED_FAIL:
      newState.scheduledEmails.loading = false;
      newState.scheduledEmails.data = [];
      return newState;

    case EMAIL_LIST.SCHEDULED_RESET:
      newState.scheduledEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.hasMoreScheduled = true;
      return newState;

    case EMAIL_LIST.RESET_ALL:
      newState.testEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.scheduledEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.sentEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.allEmails = {
        data: [] as any,
        loading: false,
        total: 0,
      };
      newState.message = '';
      newState.success = true;
      newState.hasMoreScheduled = true;
      newState.hasMoreTest = true;
      newState.hasMoreAll = true;
      newState.hasMoreSent = true;
      return newState;

    case EMAIL_LIST.SET_TAB_STATE:
      newState.tabState = action.payload.tabState;
      return newState;

    case EMAIL_LIST.RESET_TAB_STATE:
      newState.tabState = '';
      return newState;

    default:
      return state;
  }
};

const assetsInitialState = {
  data: Array<IAssetItem>(),
  paginationStatus: true,
  previouslySentEdition: Array<IPreviouslySentEditions>(),
  previouslySentEditionLoading: false,
  asset_index: undefined,
  reminder_data: {} as IPublishReminder,
  campaign_name: '',
  campaign_keywords: [],
  name: '',
  thumbnails: [],
  asset_id: 0,
  assets_count: 0,
  last_update: '',
  loading: false,
  page: 0,
  noKeywords: false,
  includeSources: [],
  excludeSources: [],
  includeKeywords: [],
  excludeKeywords: [],
};

export const assetsReducer = (state = assetsInitialState, action: any) => {
  const newState = { ...state };
  const { payload } = action;
  switch (action.type) {
    case ASSET_LIST.SUCCESS:
      newState.loading = false;
      newState.page = newState.page + 1;
      newState.data = [...state.data, ...action.payload];

      if (newState.page > 1 && payload?.length === 0) {
        newState.paginationStatus = false;
      } else if (payload?.length < 8) {
        newState.paginationStatus = false;
      } else {
        newState.paginationStatus = true;
      }

      return newState;
    case ASSET_LIST.LOAD:
      newState.loading = true;
      return newState;
    case ASSET_LIST.FAIL:
      newState.loading = false;
      return newState;

    case BROWSER_ADD_ASSET_WITH_CAMPAIGN.ADD_ASSET_IN_CAMPAIGN:
      newState.data = [action.payload, ...newState.data];
      return newState;

    case ASSET_DELETE.SUCCESS:
      let data = [...state.data];
      let newData = data.filter((it: any, ind: number) => ind !== action.payload);
      newState.data = [...newData];
      return newState;

    case ASSET_LIST.ADVANCED_SEARCH:
      let includeObj: any = [];
      let excludeObj: any = [];

      const { include_sources, exclude_sources } = action;

      // extract required keys from include sources
      include_sources.map((obj: any) => {
        includeObj.push((({ data, is_active, type }) => ({ data, is_active, type }))(obj));
      });
      // extract required keys from exclude sources
      exclude_sources.map((obj: any) => {
        excludeObj.push((({ data, is_active, type }) => ({ data, is_active, type }))(obj));
      });

      newState.includeSources = includeObj;
      newState.excludeSources = excludeObj;
      newState.includeKeywords = action.include_keywords;
      newState.excludeKeywords = action.exclude_keywords;
      if (action.include_keywords.length === 0) newState.noKeywords = true;
      return newState;

    case ASSET_LIST.RESET_ADVANCED_SEARCH:
      newState.includeSources = [];
      newState.excludeSources = [];
      newState.includeKeywords = [];
      newState.excludeKeywords = [];
      return newState;

    case ASSET_LIST.SET_CAMPAIGN_DETAILS:
      newState.campaign_name = action.campaign_name;
      newState.campaign_keywords = action.campaign_keywords;
      if (action.campaign_keywords.length === 0) newState.noKeywords = true;
      return newState;

    case ASSET_LIST.UPDATE_KEYWORDS:
      newState.campaign_keywords = action.payload.campaign_keywords;
      if (action.payload.length === 0) newState.noKeywords = true;
      else newState.noKeywords = false;
      return newState;

    case ASSET_LIST.RESET:
      newState.page = 0;
      newState.data = [];
      newState.campaign_name = '';
      newState.campaign_keywords = [];
      newState.loading = false;
      return newState;

    case PREVIOUSLY_SENT_CONTENT.LOAD_SUCCESS:
      newState.previouslySentEditionLoading = false;
      newState.previouslySentEdition = action.payload;
      return newState;
    case PREVIOUSLY_SENT_CONTENT.ADD_FAVOURITE:
      const nextState = produce(newState, (draftState) => {
        draftState.previouslySentEdition.map((li) => {
          li.articles.map((le: any) => {
            if (le.article_id == action.payload) {
              le.article_is_favourite = true;
            }
          });
        });
      });
      return nextState;
    case PREVIOUSLY_SENT_CONTENT.REMOVE_FAVOURITE:
      const draftState = produce(newState, (draftState) => {
        draftState.previouslySentEdition.map((li) => {
          li.articles.map((le: any) => {
            if (le.article_id == action.payload) {
              le.article_is_favourite = false;
            }
          });
        });
      });
      return draftState;
    case PREVIOUSLY_SENT_CONTENT.LOAD:
      newState.previouslySentEditionLoading = true;
      return newState;
    case PREVIOUSLY_SENT_CONTENT.LOAD_FAIL:
      newState.previouslySentEditionLoading = false;
      newState.previouslySentEdition = [];
      return newState;
    case GET_ASSET_REMINDER.LOAD:
      newState.reminder_data = action.payload;
      return newState;
    case GET_ASSET_REMINDER.SUCCESS:
      newState.reminder_data = action.payload;
      return newState;
    case POST_ASSET_REMINDER.SUCCESS:
      newState.reminder_data = action.payload;
      return newState;
    default:
      return state;
  }
};

const assetDetailsInitialState = {
  draftEditions: Array<IVersion>(),
  sentEditions: Array<IVersion>(),
  loading: false,
  assetName: '',
  is_active: true,
  assetKeywords: Array<string>(),
  noKeywords: false,
  assetType: '',
  assetId: '',
  updateName: {
    loading: false,
    success: false,
    fail: false,
  },
  updateKeywords: {
    loading: false,
    success: false,
    fail: false,
  },
  addNewDraft: {
    loading: false,
    success: false,
    fail: false,
  },
  removeDraft: {
    loading: false,
    success: false,
    fail: false,
  },
  page: 0,
  isSentSearch: false,
  sentEditionsLoading: false,
  searchSentPage: 0,
  searchSentLoading: false,
  searchedSentEditions: Array<IVersion>(),
  sentSearchTerm: '',
  searchTermsInCampaign: [],
};
export const assetReducer = (state = assetDetailsInitialState, action: any) => {
  const newState = { ...state };
  switch (action.type) {
    case ASSET_DETAILS.LOAD:
      newState.loading = true;
      return newState;
    case ASSET_DETAILS.DRAFT_EDITIONS_SUCCESS:
      if (action.payload.assetType === ASSET_TYPE.CUSTOM) {
        newState.draftEditions = action.payload.drafteditions;
      } else {
        newState.draftEditions = [...action.payload.drafteditions];
      }
      return newState;
    case ASSET_DETAILS.SUCCESS:
      newState.loading = false;
      if (action.payload) {
        newState.assetId = action.payload.assetId;
        newState.assetName = action.payload.assetName;
        newState.assetKeywords = action.payload.assetKeywords;
        newState.is_active = action?.payload?.isActive;
        if (action.payload.assetKeywords.length === 0) newState.noKeywords = true;
        newState.assetType = action.payload.assetType;
      }
      return newState;
    case ASSET_DETAILS.FAIL:
      newState.loading = false;
      return newState;
    case ASSET_DETAILS.SET_ACTIVE_STATE:
      newState.is_active = action.payload;
      return newState;
    case ASSET_DETAILS.RESET:
      return { ...assetDetailsInitialState };

    case ASSET_DETAILS_UPDATE_NAME.LOAD:
      newState.updateName.loading = true;
      if (action.payload) {
        newState.assetName = action.payload;
      }
      return newState;
    case ASSET_DETAILS_UPDATE_NAME.SUCCESS:
      newState.updateName.loading = false;
      newState.updateName.success = true;
      if (action.payload) {
        newState.assetName = action.payload;
      }
      return newState;
    case ASSET_DETAILS_UPDATE_NAME.FAIL:
      newState.updateName.loading = false;
      newState.updateName.fail = true;
      return newState;
    case ASSET_DETAILS_UPDATE_NAME.RESET:
      newState.updateName.loading = false;
      newState.updateName.success = false;
      newState.updateName.fail = false;
      return newState;

    case ASSET_DETAILS_UPDATE_KEYWORDS.LOAD:
      newState.updateKeywords.loading = true;
      return newState;

    case ASSET_DETAILS_UPDATE_KEYWORDS.SUCCESS:
      newState.updateKeywords.loading = false;
      newState.updateKeywords.success = true;
      let newKeywords: any = [];
      if (action.payload?.length > 0) newKeywords = [...action.payload];
      if (action.assetKeywords?.length > 0) newState.noKeywords = false;
      else newState.noKeywords = true;
      newState.assetKeywords = newKeywords;

      return newState;
    case ASSET_DETAILS_UPDATE_KEYWORDS.FAIL:
      newState.updateKeywords.loading = false;
      newState.updateKeywords.fail = true;
      return newState;
    case ASSET_DETAILS_UPDATE_KEYWORDS.RESET:
      newState.updateKeywords.loading = false;
      newState.updateKeywords.success = false;
      newState.updateKeywords.fail = false;
      return newState;

    case ASSET_DETAILS_ADD_NEW_DRAFT.LOAD:
      newState.addNewDraft.loading = true;
      return newState;
    case ASSET_DETAILS_ADD_NEW_DRAFT.SUCCESS:
      newState.addNewDraft.loading = false;
      newState.addNewDraft.success = true;
      if (action.newDraft) {
        newState.draftEditions = [action.newDraft, ...state.draftEditions];
      }
      return newState;
    case ASSET_DETAILS_ADD_NEW_DRAFT.FAIL:
      newState.addNewDraft.loading = false;
      newState.addNewDraft.fail = true;
      return newState;
    case ASSET_DETAILS_ADD_NEW_DRAFT.RESET:
      newState.addNewDraft.loading = false;
      newState.addNewDraft.success = false;
      newState.addNewDraft.fail = false;
      return newState;

    case ASSET_DETAILS_NAME.UPDATE:
      newState.assetName = action.payload;
      return newState;
    case ASSET_DETAILS_REMOVE_DRAFT.LOAD:
      newState.removeDraft.loading = true;
      return newState;
    case ASSET_DETAILS_REMOVE_DRAFT.SUCCESS:
      newState.removeDraft.loading = false;
      newState.removeDraft.success = true;
      if (action.payload.versionId) {
        newState.draftEditions = state.draftEditions.filter((item: any) => item.version_id !== action.payload.versionId);
      }
      return newState;
    case ASSET_DETAILS_REMOVE_DRAFT.FAIL:
      newState.removeDraft.loading = false;
      newState.removeDraft.fail = false;
      return newState;
    case ASSET_DETAILS_REMOVE_DRAFT.RESET:
      newState.removeDraft.loading = false;
      newState.removeDraft.success = false;
      newState.removeDraft.fail = false;
      return newState;
    case ASSET_DETAILS.UPDATE_SUBJECT:
      return newState;

    case ASSET_DETAILS_UPDATE_DRAFT.SUCCESS:
      let asset_id = action.payload.assetId;
      let version_id = action.payload.versionId;
      let articles = action.payload.articles;
      let img = action.payload.img;
      let count = typeof articles === 'number' ? 1 : articles.split(',').length;

      if (asset_id === state.assetId) {
        newState.draftEditions = state.draftEditions.map((draft: any) => {
          if (draft.version_id === version_id) {
            draft.curated_articles_count = draft.curated_articles_count + count;
            if (draft.thumbnail && draft.thumbnail.length > 2) draft.thumbnail.push(img);
            else draft.thumbnail[0] = img;
          }
          return draft;
        });
      }
      return newState;

    case ASSET_DETAILS_UPDATE_DRAFT.SET_VERSION_SUBJECT_SUCCESS:
      let updatedData = state.draftEditions.map((li) => {
        if (li.version_id === action.payload.versionId) {
          li.subject = action.payload.subject;
          li.name = action.payload.subject;
        }
        return li;
      });
      newState.draftEditions = updatedData;
      return newState;

    case ASSET_SENT_EDITIONS.LOAD:
      newState.sentEditionsLoading = true;
      return newState;
    case ASSET_SENT_EDITIONS.SUCCESS:
      newState.sentEditionsLoading = false;
      newState.page = state.page + 1;
      if (action.payload.asset_type === ASSET_TYPE.CUSTOM) {
        newState.sentEditions = action.payload.versions;
      } else {
        newState.sentEditions = [...state.sentEditions, ...action.payload.versions];
      }
      return newState;
    case ASSET_SENT_EDITIONS.FAIL:
      newState.sentEditionsLoading = false;
      return newState;
    case ASSET_SENT_EDITIONS.RESET:
      newState.sentEditionsLoading = false;
      newState.page = 0;
      newState.sentEditions = Array<IVersion>();
      return newState;

    case ASSET_SEARCH_SENT_EDITIONS.SET_SEARCH_TERM:
      newState.sentSearchTerm = action.payload;
      newState.isSentSearch = true;
      newState.searchSentPage = 0;
      newState.searchedSentEditions = [];
      return newState;

    case ASSET_SEARCH_SENT_EDITIONS.LOAD:
      newState.searchSentLoading = true;
      return newState;
    case ASSET_SEARCH_SENT_EDITIONS.SUCCESS:
      newState.searchSentLoading = false;
      newState.searchSentPage = state.searchSentPage + 1;
      newState.searchedSentEditions = [...state.searchedSentEditions, ...action.payload];
      return newState;
    case ASSET_SEARCH_SENT_EDITIONS.FAIL:
      newState.searchSentLoading = false;
      return newState;
    case ASSET_SEARCH_SENT_EDITIONS.RESET:
      newState.searchSentLoading = false;
      newState.isSentSearch = false;
      newState.searchSentPage = 0;
      newState.sentSearchTerm = '';
      newState.searchedSentEditions = Array<IVersion>();
      return newState;
    case ASSET_DETAILS.SEARCH_TERMS:
      newState.searchTermsInCampaign = action.payload;
      return newState;

    default:
      return newState;
  }
};
